/**
 * CookCard — "what's for dinner" answered in one tap. Halal-only meal idea
 * tied to season + meal time + country cuisine.
 *
 * Source: TheMealDB v1 — free, no auth, OSS-friendly. Endpoints used:
 *   - https://www.themealdb.com/api/json/v1/1/random.php   (random meal)
 *   - https://www.themealdb.com/api/json/v1/1/filter.php?a=Moroccan
 *
 * Cuisine routing by user country (default = Moroccan in MA, Indian in IN/PK,
 * etc.). Per-fetch we filter out any meal whose category/tags include "pork",
 * "wine", "alcohol", "bacon" — keeps the widget halal-safe in worst case.
 *
 * Anti-typing UX: the card always shows ONE meal (image + name + 3
 * ingredients). User taps "Try another" for a fresh pick (same fetch
 * cache pool). User taps the card to open full instructions in a sheet.
 *
 * Failure mode: if TheMealDB is down, widget hides entirely (visibilityRule
 * stays true but render produces an empty card; with the deferred-skeleton
 * pattern that's acceptable).
 */

import type { Widget, WidgetContext } from '../types';
import { swr } from '../../../services/swr';

interface Meal {
  idMeal: string;
  strMeal: string;
  strCategory?: string;
  strArea?: string;
  strInstructions?: string;
  strMealThumb?: string;
  strYoutube?: string;
  strTags?: string;
  // 20 ingredient/measure pairs (strIngredient1..20 / strMeasure1..20)
  [k: string]: string | undefined;
}

interface MealsPayload { meals: Meal[]; fetchedAt: number; }

const COUNTRY_CUISINE: Record<string, string> = {
  MA: 'Moroccan', DZ: 'Moroccan', TN: 'Moroccan',
  EG: 'Egyptian',
  TR: 'Turkish',
  IN: 'Indian', PK: 'Indian', BD: 'Indian',
  ID: 'Malaysian', MY: 'Malaysian',
  IT: 'Italian', FR: 'French', ES: 'Spanish', PT: 'Portuguese',
  GB: 'British', IE: 'Irish',
  US: 'American', CA: 'Canadian',
  MX: 'Mexican',
  CN: 'Chinese', JP: 'Japanese', VN: 'Vietnamese', TH: 'Thai',
  GR: 'Greek',
  RU: 'Russian',
  PL: 'Polish',
  NL: 'Dutch',
};

function detectCuisine(cc: string | null): string {
  if (cc && COUNTRY_CUISINE[cc]) return COUNTRY_CUISINE[cc];
  return 'Moroccan'; // good default for AWENI's primary audience
}

function isHalalFriendly(m: Meal): boolean {
  const blocklist = /pork|bacon|ham|wine|beer|alcohol|rum|whisky|sake|prosciutto/i;
  if (blocklist.test(m.strMeal || '')) return false;
  if (blocklist.test(m.strCategory || '')) return false;
  if (blocklist.test(m.strTags || '')) return false;
  // Quick scan of ingredient slots
  for (let i = 1; i <= 20; i++) {
    const ing = m[`strIngredient${i}`];
    if (typeof ing === 'string' && blocklist.test(ing)) return false;
  }
  return true;
}

async function fetchMealsByArea(area: string): Promise<MealsPayload | null> {
  // Fetch the full filter list (light, names only) then pull 1 random by id.
  try {
    const filterResp = await fetch(`https://www.themealdb.com/api/json/v1/1/filter.php?a=${encodeURIComponent(area)}`, { cache: 'no-store' });
    if (!filterResp.ok) return null;
    const filter = await filterResp.json() as { meals: Array<{ idMeal: string }> | null };
    const ids = (filter.meals || []).map((m) => m.idMeal);
    if (ids.length === 0) return null;
    // Pick up to 6 random ids and resolve full detail for each (so we have a
    // local pool to rotate through without hammering TheMealDB on every tap).
    const shuffled = ids.sort(() => Math.random() - 0.5).slice(0, 6);
    const details: Meal[] = [];
    for (const id of shuffled) {
      try {
        const r = await fetch(`https://www.themealdb.com/api/json/v1/1/lookup.php?i=${encodeURIComponent(id)}`, { cache: 'no-store' });
        if (!r.ok) continue;
        const d = await r.json() as { meals: Meal[] | null };
        if (d.meals && d.meals[0]) details.push(d.meals[0]);
      } catch { /* skip */ }
    }
    const safe = details.filter(isHalalFriendly);
    if (safe.length === 0) return null;
    return { meals: safe, fetchedAt: Date.now() };
  } catch {
    return null;
  }
}

function ingredientsOf(m: Meal, max: number): string[] {
  const out: string[] = [];
  for (let i = 1; i <= 20 && out.length < max; i++) {
    const ing = m[`strIngredient${i}`];
    if (typeof ing === 'string' && ing.trim()) out.push(ing.trim());
  }
  return out;
}

export const CookCard: Widget = {
  id: 'cook-card',
  label: {
    en: 'Cook',
    fr: 'Cuisiner',
    ar: 'اطبخ',
    es: 'Cocinar',
    de: 'Kochen',
  },
  icon: '🍳',
  defaultVisible: true,
  defaultOrder: 23,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const loc = ctx.getLocationService();
    const cuisine = detectCuisine(loc.countryCode);

    container.innerHTML = `
      <div data-cook-shell style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;overflow:hidden;">
        <div style="display:flex;align-items:center;gap:8px;padding:14px 14px 0 14px;">
          <span style="font-size:18px;">🍳</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_cook_title') || 'Cook tonight')}</span>
          <span style="margin-inline-start:auto;font-size:11px;color:var(--ln-text-secondary,#6b6657);">${escapeHtml(cuisine)}</span>
        </div>
        <div data-cook-body style="padding:10px 14px 14px;">
          <div style="height:120px;background:var(--ln-bg-secondary,rgba(0,0,0,0.03));border-radius:12px;" class="skeleton-pulse"></div>
        </div>
      </div>
    `;

    const body = container.querySelector('[data-cook-body]') as HTMLElement | null;
    let pool: Meal[] = [];
    let cursor = 0;

    const renderMeal = (m: Meal): void => {
      if (!body) return;
      const ings = ingredientsOf(m, 4);
      const img = m.strMealThumb || '';
      body.innerHTML = `
        <button type="button" data-act="open" style="display:block;width:100%;border:none;padding:0;cursor:pointer;background:transparent;text-align:start;color:inherit;">
          <div style="position:relative;border-radius:12px;overflow:hidden;background:#0001;aspect-ratio:16/9;">
            ${img ? `<img loading="lazy" src="${escapeAttr(img)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" />` : ''}
            <div style="position:absolute;inset:0;background:linear-gradient(to top, rgba(0,0,0,0.55), transparent 60%);"></div>
            <div style="position:absolute;inset-inline-start:12px;bottom:10px;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,0.4);">
              <div style="font-size:14px;font-weight:800;line-height:1.2;">${escapeHtml(m.strMeal)}</div>
              <div style="font-size:11px;opacity:0.85;">${escapeHtml(m.strCategory || '')} · ${escapeHtml(m.strArea || '')}</div>
            </div>
          </div>
        </button>
        <div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:8px;">
          ${ings.map((ing) => `<span style="font-size:11px;padding:3px 8px;border-radius:999px;background:var(--ln-bg-secondary,rgba(0,0,0,0.04));color:var(--ln-text-primary,#1E293B);">${escapeHtml(ing)}</span>`).join('')}
        </div>
        <div style="display:flex;gap:6px;margin-top:8px;">
          <button type="button" data-act="another" style="flex:1;min-height:36px;padding:6px 10px;border-radius:10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));background:transparent;font-size:12px;cursor:pointer;color:inherit;">⟳ ${escapeHtml(t('widget_cook_another') || 'Try another')}</button>
          <button type="button" data-act="open" style="flex:1;min-height:36px;padding:6px 10px;border-radius:10px;border:1px solid var(--ln-accent,#0A0A0A);background:var(--ln-accent,#0A0A0A);color:var(--ln-accent-text,#fff);font-size:12px;font-weight:600;cursor:pointer;">📖 ${escapeHtml(t('widget_cook_recipe') || 'Recipe')}</button>
        </div>
      `;
      body.querySelectorAll<HTMLButtonElement>('[data-act="another"]').forEach((b) => b.addEventListener('click', () => {
        if (pool.length === 0) return;
        cursor = (cursor + 1) % pool.length;
        renderMeal(pool[cursor]);
      }));
      body.querySelectorAll<HTMLButtonElement>('[data-act="open"]').forEach((b) => b.addEventListener('click', () => {
        openRecipeSheet(m, ctx);
      }));
    };

    swr<MealsPayload>(
      `meals_${cuisine}`,
      () => fetchMealsByArea(cuisine),
      {
        freshTtlMs: 6 * 60 * 60 * 1000,
        staleTtlMs: 7 * 24 * 60 * 60 * 1000,
        onUpdate: (data) => { pool = data.meals; cursor = 0; if (pool[0]) renderMeal(pool[0]); },
      },
    ).then((data) => {
      if (data && data.meals && data.meals.length > 0) {
        pool = data.meals;
        cursor = Math.floor(Math.random() * pool.length);
        renderMeal(pool[cursor]);
      } else if (body) {
        body.innerHTML = `<div style="font-size:12px;color:var(--ln-text-secondary,#6b6657);padding:12px 0;">${escapeHtml(t('widget_cook_offline') || 'Recipes offline. Will refresh later.')}</div>`;
      }
    }).catch(() => { /* silent */ });
  },
};

function openRecipeSheet(m: Meal, ctx: WidgetContext): void {
  // Lightweight in-place sheet — no router navigation, no module load.
  // The recipe is read from the already-fetched meal object so opening is
  // instant. Closing returns to the home unchanged.
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:60;animation:askfabOverlayIn var(--motion-duration-base) var(--motion-easing-default);';
  overlay.addEventListener('click', () => { overlay.remove(); sheet.remove(); });

  const sheet = document.createElement('div');
  sheet.style.cssText = `position:fixed;inset-inline:0;bottom:0;max-height:85vh;overflow-y:auto;background:var(--ln-bg-card,#fff);border-top-left-radius:20px;border-top-right-radius:20px;padding:16px 16px calc(16px + env(safe-area-inset-bottom));z-index:61;animation:askfabSheetIn var(--motion-duration-base) var(--motion-easing-emphasized);box-shadow:0 -8px 24px rgba(0,0,0,0.18);color:var(--ln-text-primary,#1E293B);`;
  sheet.setAttribute('dir', ctx.isRtl ? 'rtl' : 'ltr');

  const ings: string[] = [];
  for (let i = 1; i <= 20; i++) {
    const ing = m[`strIngredient${i}`];
    const meas = m[`strMeasure${i}`];
    if (typeof ing === 'string' && ing.trim()) {
      ings.push(`${meas?.trim() || ''} ${ing.trim()}`.trim());
    }
  }
  const yt = m.strYoutube ? `<a href="${escapeAttr(m.strYoutube)}" target="_blank" rel="noopener noreferrer" style="color:var(--ln-accent-ceremonial,#9C8348);text-decoration:underline;">▶ Video</a>` : '';

  sheet.innerHTML = `
    <div style="width:44px;height:4px;border-radius:999px;background:rgba(0,0,0,0.15);margin:0 auto 12px;"></div>
    <div style="font-size:18px;font-weight:800;">${escapeHtml(m.strMeal)}</div>
    <div style="font-size:12px;color:var(--ln-text-secondary,#6b6657);margin-bottom:10px;">${escapeHtml(m.strCategory || '')} · ${escapeHtml(m.strArea || '')} ${yt ? `· ${yt}` : ''}</div>
    ${m.strMealThumb ? `<img src="${escapeAttr(m.strMealThumb)}" alt="" loading="lazy" style="width:100%;border-radius:12px;display:block;margin-bottom:12px;" />` : ''}
    <div style="font-weight:700;font-size:13px;margin-bottom:6px;">${escapeHtml(ctx.t('widget_cook_ingredients') || 'Ingredients')}</div>
    <ul style="margin:0 0 12px;padding-inline-start:18px;font-size:13px;line-height:1.55;">
      ${ings.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}
    </ul>
    <div style="font-weight:700;font-size:13px;margin-bottom:6px;">${escapeHtml(ctx.t('widget_cook_instructions') || 'Steps')}</div>
    <div style="font-size:13px;line-height:1.6;white-space:pre-wrap;">${escapeHtml(m.strInstructions || '')}</div>
    <button type="button" data-act="close" style="margin-top:14px;width:100%;padding:12px;border:none;border-radius:12px;background:var(--ln-accent,#0A0A0A);color:var(--ln-accent-text,#fff);font-weight:700;cursor:pointer;">${escapeHtml(ctx.t('common_close') || 'Close')}</button>
  `;
  sheet.querySelector('[data-act="close"]')?.addEventListener('click', () => { overlay.remove(); sheet.remove(); });

  document.body.appendChild(overlay);
  document.body.appendChild(sheet);
}

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
